import Vue from 'vue';
import contactService from '../../api/contact-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '../../util/event-bus';
import i18n from '../../i18n/i18n-config';
import { updateArrayItem, removeArrayItem } from '../../util/array';
import { mapErrorsToInputs } from '../../util/forms';
import imageService from '../../api/image-service';

const state = {
  contacts: null,
  contactPagination: {},
  editedContact: {},
  contactValidationErrors: {},
  contactFilterParams: {},
  loadingContacts: false,
  savingContact: false,
  savingImage: false,
};

const mutations = {
  SET_CONTACTS(state, { data, current_page, per_page, total }) {
    state.contacts = data;
    state.contactPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.contactFilterParams = params;
  },

  SET_EDITED_CONTACT(state, contact) {
    state.contactValidationErrors = {};
    state.editedContact = JSON.parse(JSON.stringify(contact));
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.contactValidationErrors[field];
  },

  STORE_CONTACT(state, contact) {
    state.contacts = [...state.contacts, contact];
    state.contactValidationErrors = {};
    state.contactPagination.total += 1;
  },

  UPDATE_CONTACT(state, contact) {
    state.contacts = updateArrayItem(state.contacts, contact);
  },

  DELETE_CONTACT(state, contact) {
    state.contacts = removeArrayItem(state.contacts, contact);
    state.contactPagination.total -= 1;
  },

  SET_CONTACT_VALIDATION_ERRORS(state, contactValidationErrors) {
    state.contactValidationErrors = contactValidationErrors;
  },

  SET_LOADING_CONTACTS(state, loading) {
    state.loadingContacts = loading;
  },

  SET_SAVING_CONTACT(state, saving) {
    state.savingContact = saving;
  },

  SET_SAVING_IMAGE(state, saving) {
    state.savingImage = saving;
  },

  SET_CONTACT_IMAGE(state, image) {
    Vue.set(state.editedContact, 'image', image);
    for (let i = 0; i < state.contacts?.length; i++) {
      if (state.contacts[i].id === +image.model_id) {
        Vue.set(state.contacts[i], 'image', image);
        break;
      }
    }
  },

  DELETE_CONTACT_IMAGE(state, image) {
    Vue.set(state.editedContact, 'image', null);
    for (let i = 0; i < state.contacts?.length; i++) {
      if (state.contacts[i].id === +image.model_id) {
        Vue.set(state.contacts[i], 'image', null);
        break;
      }
    }
  },
};

const actions = {
  fetchContacts({ commit }, params) {
    commit('SET_LOADING_CONTACTS', true);
    return contactService
      .getPage(params)
      .then((res) => {
        commit('SET_CONTACTS', res.data);
        if (params.page !== state.contactFilterParams.page) {
          window.scrollTo(0, 0);
        }
        commit('SET_FILTER_PARAMS', params);
      })
      .finally(() => {
        commit('SET_LOADING_CONTACTS', false);
      });
  },

  async storeContact({ commit }) {
    commit('SET_SAVING_CONTACT', true);
    const res = await contactService.create();
    commit('SET_SAVING_CONTACT', false);
    commit('STORE_CONTACT', res.data);
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('contact_created'));
    return res;
  },

  editContact({ state, commit }, contactId) {
    const contact = state.contacts?.find((c) => c.id === contactId);
    if (contact) {
      commit('SET_EDITED_CONTACT', contact);
      return Promise.resolve(contact);
    }
    return contactService.getById(contactId).then((res) => {
      commit('SET_EDITED_CONTACT', res.data);
    });
  },

  updateContact({ commit }, contact) {
    commit('SET_SAVING_CONTACT', true);
    return contactService
      .update(contact)
      .then((res) => {
        commit('UPDATE_CONTACT', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('contact_updated'));
      })
      .catch((err) => {
        commit('SET_CONTACT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      })
      .finally(() => commit('SET_SAVING_CONTACT', false));
  },

  deleteContact({ commit }, contact) {
    openConfirmDialog({
      title: i18n.t('confirm_contact_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      contactService.delete(contact).then(() => {
        commit('DELETE_CONTACT', contact);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('contact_deleted'));
      });
    });
  },

  async uploadContactImage({ commit }, { image, type, modelType, modelId }) {
    commit('SET_SAVING_IMAGE', true);
    eventBus.$emit(OPEN_SNACKBAR, {
      text: i18n.t('uploading'),
      showProgress: true,
      timeout: -1,
    });
    const res = await imageService.upload(image, type, modelType, modelId);
    commit('SET_SAVING_IMAGE', false);
    commit('SET_CONTACT_IMAGE', res.data[0]);
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('image_uploaded'));
    return res;
  },

  deleteContactImage({ commit }, image) {
    openConfirmDialog({
      title: i18n.t('confirm_image_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      imageService.delete(image).then(() => {
        commit('DELETE_CONTACT_IMAGE', image);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('image_deleted'));
      });
    });
  },

  clearContactValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
